<template>
	<span v-if="_canGenerateIa">
		<b-button
			v-if="!inline"
			variant="link"
			:title="title"
			@click="$emit('enter-test-mode')">
			<i class="fas fa-magic"></i>
		</b-button>

		<codegen-shell :inline="inline" :modal-id="'codegen-' + id" :title="modalTitle">
			<b-container style="margin: 0; padding: 0; max-width: none;">
				<b-row>
					<!-- Liste des conversations (chats) -->
					<b-col :cols="3" style="border-right: 1px solid #dee2e6;">
						<b-button block size="sm" variant="outline-primary" class="mb-2" @click="newChat">
							<i class="fas fa-plus"></i> Nouveau chat
						</b-button>
						<div v-if="chats.length === 0" class="text-muted small">Aucune conversation</div>
						<div
							v-for="chat in chats"
							:key="chat.uid"
							class="d-flex align-items-center mb-1">
							<b-form-input
								v-if="editingChatUid === chat.uid"
								v-model="editingChatTitle"
								size="sm"
								class="flex-grow-1"
								autofocus
								@keydown.enter.native="commitChatTitle(chat)"
								@keydown.esc.native="cancelChatTitleEdit"
								@blur="commitChatTitle(chat)"
							/>
							<div
								v-else
								class="flex-grow-1 p-1 rounded"
								style="cursor: pointer; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
								:style="{ background: chat.uid === activeChatUid ? '#e9ecef' : 'transparent' }"
								:title="chat.title"
								@click="selectChat(chat)"
								@dblclick="startChatTitleEdit(chat)">
								<small>{{ chat.title || "(sans titre)" }}</small>
							</div>
							<b-button
								v-if="editingChatUid !== chat.uid"
								size="sm"
								variant="link"
								class="p-0 ml-1"
								title="Renommer cette conversation"
								@click="startChatTitleEdit(chat)">
								<i class="fas fa-pencil-alt"></i>
							</b-button>
							<b-button
								size="sm"
								variant="link"
								class="text-danger p-0 ml-1"
								title="Supprimer cette conversation"
								@click="removeChat(chat)">
								<i class="fas fa-trash"></i>
							</b-button>
						</div>
					</b-col>

					<!-- Conversation active -->
					<b-col :cols="9">
						<div v-if="activeChat" class="mb-2">
							<b-button
								v-b-toggle="'codegen_engine_' + id"
								size="sm"
								variant="link"
								class="p-0 text-muted">
								<i class="fas fa-key"></i> Moteur IA : {{ engineSummaryLabel }}
								<i class="fas fa-caret-down"></i>
							</b-button>
							<b-collapse :id="'codegen_engine_' + id">
								<div class="d-flex align-items-center flex-wrap mt-1 p-2" style="background:#f8f9fa; border-radius:4px;">
									<b-form-radio-group
										v-if="ownCredentialsAvailable"
										v-model="activeChat.use_own_credentials"
										:options="[{value: false, text: 'IA partagée (SMD)'}, {value: true, text: 'Ma clé ChatGPT'}]"
										buttons
										button-variant="outline-primary"
										size="sm"
									/>
									<span v-else class="small text-muted">IA partagée (SMD)</span>
									<template v-if="activeChat.use_own_credentials">
										<b-select
											v-model="activeChat.model_preset"
											:options="list_models"
											size="sm"
											style="display:inline-block; width:auto; margin-left:8px; margin-right:6px;"
										/>
										<b-button size="sm" variant="outline-secondary" @click="open_model_editor" v-b-tooltip.hover :title="activeChat.model_preset ? 'Modifier ce modèle' : 'Créer un nouveau modèle'">
											<i :class="activeChat.model_preset ? 'fas fa-pen' : 'fas fa-plus'"></i>
										</b-button>
									</template>

									<!-- Détection/config de la clé ChatGPT personnelle : on RÉUTILISE tel quel
									     le composant StepCredentialField (même composant que le bouton clé de la
									     toolbar de Step.vue, déjà éprouvé) au lieu de ré-implémenter une détection
									     maison — qui, elle, ne reconnaissait pas une clé pourtant bien configurée. -->
									<div class="w-100 mt-2">
										<b-button v-b-toggle="'codegen_own_key_' + id" size="sm" variant="link" class="p-0">
											<i class="fas fa-key"></i> Gérer ma clé ChatGPT
										</b-button>
										<b-collapse :id="'codegen_own_key_' + id" class="mt-1">
											<step-credential-field
												ref="ownCredClient"
												credential-name="chatgpt_key"
												label="Clé ChatGPT (OpenAI) — commune (client)"
												scope="client"
												@status="onOwnCredentialStatus" />
											<step-credential-field
												ref="ownCredUser"
												credential-name="chatgpt_key"
												label="Clé ChatGPT (OpenAI) — personnelle (moi)"
												scope="user"
												@status="onOwnCredentialStatus" />
										</b-collapse>
									</div>

									<div class="w-100 mt-2">
										<b-button
											v-if="!isCurrentEngineDefault"
											size="sm"
											variant="outline-secondary"
											@click="setAsDefaultEngine">
											<i class="fas fa-thumbtack"></i> Définir comme réglage par défaut
										</b-button>
										<span v-else class="small text-success"><i class="fas fa-check"></i> Réglage par défaut pour les nouvelles conversations</span>
										<b-button size="sm" variant="link" class="p-0 ml-2 text-muted" v-b-tooltip.hover title="Ce choix (SMD ou clé propre + modèle) est propre à cette conversation. Il ne s'applique pas aux autres conversations, sauf si vous cliquez sur 'Définir comme réglage par défaut' — dans ce cas il sera proposé automatiquement pour toute NOUVELLE conversation créée ensuite.">
											<i class="fas fa-info-circle"></i>
										</b-button>
									</div>
								</div>
							</b-collapse>
						</div>

						<div
							v-if="activeChat && activeChat.messages.length > 0"
							class="mb-2"
							style="max-height: 300px; overflow-y: auto; background: #f8f9fa; padding: 6px; border-radius: 4px;">
							<div
								v-for="(m, idx) in activeChat.messages"
								:key="idx"
								class="small mb-1"
								:style="isMessageOmitted(idx) ? 'opacity: 0.45;' : ''"
								:title="isMessageOmitted(idx) ? 'Ce message ne sera pas envoyé à l\'IA au prochain tour (hors de la fenêtre de contexte, cf message ci-dessous).' : ''">
								<div style="cursor: pointer;" @click="toggleHistoryMessage(idx)">
									<i :class="isHistoryMessageExpanded(idx) ? 'fas fa-chevron-down' : 'fas fa-chevron-right'" style="width: 14px; display: inline-block;"></i>
									<b>{{ m.role === 'user' ? 'Vous' : 'IA' }} :</b>
									<i v-if="isMessageOmitted(idx)" class="fas fa-eye-slash text-muted ml-1" v-b-tooltip.hover title="Non envoyé à l'IA au prochain tour (hors fenêtre de contexte)."></i>
									<span v-if="!isHistoryMessageExpanded(idx)" style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">{{ truncateForHistory(m.content, 50) }}</span>
								</div>
								<div v-if="isHistoryMessageExpanded(idx)" style="white-space: pre-wrap; margin-left: 16px;">{{ fullHistoryContent(m.content) }}</div>
							</div>
							<div v-if="historyOmittedCount > 0" class="small text-muted border-top mt-1 pt-1">
								<i class="fas fa-eye-slash"></i> {{ historyOmittedCount }} message(s) grisé(s) ci-dessus ({{ historyOmittedCount === 1 ? 'ne sera' : 'ne seront' }} pas envoyé{{ historyOmittedCount === 1 ? '' : 's' }} à l'IA au prochain tour : le tout premier message (instructions + code de référence) et les échanges les plus récents restent transmis, mais la conversation est trop longue pour tout renvoyer à chaque tour (coût/latence, cf paramètre de taille de contexte).
							</div>
						</div>

						<b-alert :show="isDivergent" variant="warning">
							Le code a changé depuis la dernière proposition de cette conversation (édition manuelle ou autre génération entre-temps).
							Choisissez comment continuer, puis saisissez votre prompt et cliquez sur "Générer le code" :
							<div class="mt-2">
								<b-button
									size="sm"
									variant="warning"
									title="Garde l'historique de cette conversation tel quel (l'IA continue avec ce qu'elle connaît déjà), en ignorant simplement la divergence détectée."
									@click="resolveDivergence('continue')">
									Continuer quand même
								</b-button>
								<b-button
									size="sm"
									variant="outline-warning"
									class="ml-2"
									title="Garde l'historique de cette conversation, mais ajoute un message indiquant le nouveau code actuel : l'IA en tiendra compte pour la suite des échanges."
									@click="resolveDivergence('rebase')">
									Repartir du code actuel
								</b-button>
								<b-button
									size="sm"
									variant="outline-secondary"
									class="ml-2"
									title="Démarre une toute nouvelle conversation vide à partir du code actuel : cette conversation-ci reste inchangée et reste disponible dans la liste."
									@click="newChat">
									Démarrer un nouveau chat
								</b-button>
							</div>
						</b-alert>

						<p>
							<span class="d-flex align-items-center justify-content-between mb-1">
								<span>Prompt</span>
								<b-dropdown v-if="columns && columns.length > 0" size="sm" variant="link" toggle-class="text-decoration-none p-0" no-caret right>
									<template #button-content>
										<i class="fas fa-columns"></i> Insérer une colonne
									</template>
									<b-dropdown-form @submit.prevent.stop>
										<b-form-input
											v-model="columnFilter"
											size="sm"
											autocomplete="off"
											placeholder="Filtrer..."
											@click.stop
										/>
									</b-dropdown-form>
									<b-dropdown-divider></b-dropdown-divider>
									<b-dropdown-item
										v-for="col in filteredColumns"
										:key="col"
										@click="insertColumnInPrompt(col)">
										{{ col }}
									</b-dropdown-item>
									<b-dropdown-text v-if="filteredColumns.length === 0" class="text-muted small">
										Aucune colonne
									</b-dropdown-text>
								</b-dropdown>
							</span>
							<div v-if="activeChat" class="d-flex align-items-center mb-2">
								<small class="text-muted mr-2">Réponse de l'IA :</small>
								<b-form-radio-group
									v-model="activeChat.response_mode"
									:options="responseModeOptions"
									:disabled="loading"
									buttons
									button-variant="outline-secondary"
									size="sm"
									name="response-mode"
								/>
								<b-button
									size="sm"
									variant="link"
									class="p-0 ml-1 text-muted"
									v-b-tooltip.hover
									title="Code : l'IA modifie uniquement le code (comportement historique). Code + réponse : l'IA peut à la fois expliquer et proposer du code. Réponse seule : discussion uniquement, le code de l'étape n'est jamais modifié.">
									<i class="fas fa-info-circle"></i>
								</b-button>
							</div>
							<b-form-textarea
								ref="promptInput"
								v-model="prompt"
								rows="4"
								max-rows="12"
								:placeholder="promptPlaceholder"
							/>
						</p>

						<div class="mb-3">
							<b-button variant="primary" :disabled="!canSendPrompt" @click="generate()" title="Envoie ce prompt à l'IA pour générer ou modifier le code de l'étape, ou pour répondre à une question de clarification (réponses ci-dessous + texte libre ci-dessus sont envoyés ensemble).">
								<i v-if="!loading" class="fas fa-paper-plane"></i>
								<span v-if="!loading">{{ sendButtonLabel }}</span>
								<span v-else>Génération…</span>
							</b-button>
						</div>

						<slot name="options"></slot>

						<div v-if="lastAssistantText" class="mb-2 p-2" style="background:#eef6ff;border:1px solid #cfe2ff;border-radius:4px; white-space: pre-wrap;">
							<i class="fas fa-comment-alt text-primary"></i> {{ lastAssistantText }}
						</div>

						<div v-if="activeChatQuestions.length > 0" class="mb-3">
							<div
								v-for="(q, qIdx) in activeChatQuestions"
								:key="qIdx"
								class="mb-2 p-2"
								style="background:#f8f9fa;border:1px solid #dee2e6;border-radius:4px;">
								<p class="mb-2"><i class="fas fa-question-circle text-primary mr-1"></i><b>{{ q.question }}</b></p>
								<div v-if="q.options.length > 0" class="d-flex flex-wrap mb-2">
									<b-button
										v-for="(opt, oIdx) in q.options"
										:key="oIdx"
										size="sm"
										:variant="questionAnswers[qIdx] === opt ? 'primary' : 'outline-primary'"
										class="mr-2 mb-2"
										:disabled="loading"
										@click="selectQuestionOption(qIdx, opt)">
										{{ opt }}
									</b-button>
								</div>
								<b-form-input
									v-model="questionAnswers[qIdx]"
									size="sm"
									:disabled="loading"
									placeholder="Ou répondez librement ici..."
								/>
							</div>
							<p class="small text-muted mb-0">
								<i class="fas fa-info-circle"></i> Répondez à une ou plusieurs questions ci-dessus (option suggérée et/ou texte libre), ajoutez éventuellement des précisions dans le champ "Prompt" plus haut, puis cliquez sur "{{ sendButtonLabel }}" : tout est envoyé ensemble.
							</p>
						</div>

						<div v-if="stage === 'review'">
							<p v-if="validation_errors.length > 0" style="color: #b00020">
								<b>Erreurs de validation</b>
								<ul>
									<li v-for="(err, idx) in validation_errors" :key="idx">{{ err }}</li>
								</ul>
							</p>
							<b-container style="margin: 0; padding: 0; max-width: none;">
								<b-row>
									<b-col :cols="12">
										<b>Diff — code avant génération / code généré</b>
										<codemirror-unified-diff
											ref="diffEditor"
											:original.sync="diffOriginal"
											:value="lastGeneratedCode"
											:height="360"
											:language="codeLanguage"
											@input="onDiffInput"
											@update:original="onDiffOriginalUpdate"
										/>
									</b-col>
								</b-row>
							</b-container>
						</div>

						<div v-else-if="stage === 'prompt' && value">
							<b-container style="margin: 0; padding: 0; max-width: none;">
								<b-row>
									<b-col :cols="12">
										<b>Code actuel de l'étape</b>
										<codemirror-unified-diff
											:original="value"
											:value="value"
											:height="280"
											:language="codeLanguage"
											:read-only="true"
										/>
									</b-col>
								</b-row>
							</b-container>
						</div>

						<p v-if="error" style="color: #b00020; white-space: pre-wrap">{{ error }}</p>

						<div class="d-flex justify-content-between align-items-center mt-3">
							<b-button v-if="!inline" variant="secondary" @click="$bvModal.hide('codegen-' + id)">
								<i class="fas fa-times"></i> Fermer
							</b-button>
							<span v-else></span>
							<div class="d-flex align-items-center flex-wrap">
								<template v-if="stage === 'review'">
									<b-button-group size="sm" class="mr-2">
										<b-button
											variant="outline-success"
											:disabled="!lastGeneratedCode"
											title="Accepter tous les blocs modifiés restants du diff"
											@click="acceptAllHunks">
											<i class="fas fa-check-double"></i> Tout accepter
										</b-button>
										<b-button
											variant="outline-danger"
											:disabled="!lastGeneratedCode"
											title="Refuser tous les blocs modifiés restants du diff"
											@click="rejectAllHunks">
											<i class="fas fa-ban"></i> Tout refuser
										</b-button>
									</b-button-group>
									<span class="mr-2" style="border-left: 1px solid #dee2e6; height: 24px;"></span>
									<b-button
										variant="outline-warning"
										:disabled="!codeBeforeGeneration"
										title="Annule le dernier prompt envoyé : restaure le code tel qu'il était juste avant, sans toucher aux prompts précédents de cette conversation."
										@click="revertToCodeBeforeGeneration">
										<i class="fas fa-undo"></i> Annuler ce dernier prompt
									</b-button>
									<b-button
										v-if="!inline"
										class="ml-2"
										variant="success"
										:disabled="!lastGeneratedCode || validation_errors.length > 0"
										title="Applique le code du diff ci-dessus (après accept/reject) à l'étape"
										@click="applyGeneratedCode">
										<i class="fas fa-check"></i> Appliquer le code généré
									</b-button>
									<span v-if="!inline" class="mx-3" style="border-left: 1px solid #dee2e6; height: 24px;"></span>
								</template>
							</div>
						</div>
					</b-col>
				</b-row>
			</b-container>
		</codegen-shell>

		<!-- Éditeur de preset de modèle (section 7.3), même mécanisme que
		     steps/chatgpt.vue mais restreint au service "chatgpt". -->
		<b-modal
			:id="'codegen_model_editor' + id"
			:title="model_editor_is_new ? 'Nouveau modèle' : 'Modifier le modèle'"
			@ok="save_model"
			ok-title="Enregistrer"
			cancel-title="Annuler"
		>
			<b-form-group label="Nom du modèle">
				<b-input v-model="model_editor_name" :disabled="!model_editor_is_new" placeholder="ex: mon-modele-code" />
			</b-form-group>
			<b-form-group label="Modèle">
				<div class="d-flex align-items-center mb-1">
					<b-input v-model="model_editor_version" :list="'codegen_model_editor_versions' + id" placeholder="ex: gpt-4.1, gpt-4o, o4-mini..." style="margin-right:6px;" />
					<b-button size="sm" variant="outline-secondary" @click="fetch_available_models" :disabled="model_editor_loading_models" v-b-tooltip.hover title="Récupérer la liste des modèles disponibles">
						<i v-if="model_editor_loading_models" class="fas fa-spinner fa-spin"></i>
						<i v-else class="fas fa-sync"></i>
					</b-button>
				</div>
				<datalist :id="'codegen_model_editor_versions' + id">
					<option v-for="(m, idx) in model_editor_available_models" :key="idx" :value="m.value">{{ m.text }}</option>
				</datalist>
			</b-form-group>
			<div v-if="model_editor_saving" class="text-center"><i class="fas fa-spinner fa-spin"></i> Enregistrement...</div>
			<div v-if="model_editor_error" class="text-danger">{{ model_editor_error }}</div>
		</b-modal>

	</span>
</template>

<script>
import uniqueId from "lodash.uniqueid";
import workers from "../services/workers.js";
import treatments from "../services/treatments.js";
import library from "../services/library.js";
import * as json2csv from "json2csv";
import clone from "clone";
import CryptoJS from "crypto-js";
import moment from "moment";
import store from "../store";
import codemirrorUnifiedDiff from "./codemirror_unified_diff.vue";
import StepCredentialField from "./StepCredentialField.vue";
import codegenSettings from "../services/codegen_settings.js";
import codegenShell from "./codegen_shell.vue";

// Budget de taille (en caractères, hors message d'ancrage) pour la fenêtre
// d'historique envoyée à l'IA à chaque tour (perf, cf TODO section 5.1).
// Remplace l'ancienne fenêtre à nombre de messages fixe (6) par un budget en
// caractères : plus flexible, s'adapte à la taille réelle des échanges (ex.
// petits algos JS d'une trentaine/cinquantaine de lignes dans un script
// personnalisé → beaucoup de tours tiennent dans le budget ; gros blocs HTML
// de tableau de bord → moins de tours tiennent, mais aucun n'est coupé en
// plein milieu). Le message d'ancrage (1er message, instructions + code de
// référence) et le nouveau message en cours d'envoi ne sont jamais comptés
// dans ce budget : ils sont toujours envoyés intégralement, quelle que soit
// leur taille.
const MESSAGES_HISTORY_CHAR_BUDGET = 20000;

export default {
	name: "CodeGenerator",
	components: {
		codemirrorUnifiedDiff,
		StepCredentialField,
		codegenShell
	},
	props: {
		value: {
			type: String,
			default: ""
		},
		// Mode "test en place" (TODO_CODEGEN_CHAT.md section 10, proposition v4) :
		// remplace la modale par un bloc affiché directement là où le parent le
		// place dans son propre template (ex. à la place de l'éditeur Ace), sans
		// backdrop ni verrouillage de l'écran par ce composant lui-même — c'est au
		// parent (ex. steps/fonction.vue) de griser le reste de son propre contexte
		// pendant ce mode. Voir codegen_shell.vue pour l'habillage (modale vs bloc
		// en ligne), le contenu du panneau reste strictement identique.
		inline: {
			type: Boolean,
			default: false
		},
		buildRequest: {
			type: Function,
			required: true
		},
		title: {
			type: String,
			default: "Remplace temporairement l'éditeur par le générateur : discutez avec l'IA et testez le code proposé avant de l'appliquer, sans perdre le code actuel."
		},
		modalTitle: {
			type: String,
			default: "Génération de code"
		},
		promptPlaceholder: {
			type: String,
			default: "Décrivez ce que vous voulez générer..."
		},
		validateCode: {
			type: Function,
			default: null
		},
		// Langage du code généré, pour adapter la coloration syntaxique du diff
		// (codemirror_unified_diff.vue) : "javascript" (défaut, cf steps/fonction.vue),
		// "html" (dashboard/textBuilder.vue, qui génère du HTML) ou "vue"
		// (dashboard/vueComponentBuilder.vue, qui génère un fichier Vue SFC complet
		// avec <template>/<script>/<style>), cf TODO_CODEGEN_CHAT.md section 10.
		codeLanguage: {
			type: String,
			default: "javascript"
		},
		// Fonction async optionnelle (fournie par le parent) permettant de
		// charger paresseusement le contenu complet d'un chat référencé
		// uniquement par {uid, url} (cf section 4 : persistance S3). Signature :
		// async (chatRef) => { messages, lastGeneratedCode, lastKnownLiveCodeHash, codeBeforeGeneration }
		loadChat: {
			type: Function,
			default: null
		},
		// Fonction async optionnelle (fournie par le parent) appelée pour
		// supprimer définitivement un chat (et son éventuel fichier S3associé).
		// Signature : async (chatRef) => void
		deleteChat: {
			type: Function,
			default: null
		},
		// Liste optionnelle des colonnes disponibles (fichier/source de l'étape
		// courante, ex. columns_data de steps/fonction.vue) : si fournie et non
		// vide, affiche un menu "Insérer une colonne" sous le prompt permettant de
		// l'insérer directement dans le texte sans avoir à retaper son nom (cf
		// TODO_CODEGEN_CHAT.md section 10).
		columns: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
			id: uniqueId(""),
			stage: "prompt",
			prompt: "",
			loading: false,
			error: "",
			validation_errors: [],
			// Filtre texte du menu "Insérer une colonne" (état purement transitoire,
			// non persisté, remis à vide à chaque insertion).
			columnFilter: "",
			// Tableau des conversations (chats) de ce générateur. Chaque chat :
			// { uid, title, messages: [{role, content}], lastGeneratedCode,
			//   lastKnownLiveCodeHash, codeBeforeGeneration, diffOriginal,
			//   loaded, url, updated_at, _snapshotHash }
			// (voir TODO_CODEGEN_CHAT.md sections 3 et 4)
			chats: [],
			activeChatUid: "",
			// Renommage du titre d'un chat (section 10, "Renommer les titres des
			// chats") : édition inline (crayon ou double-clic), état purement
			// transitoire (UI), un seul chat éditable à la fois.
			editingChatUid: "",
			editingChatTitle: "",
			// État d'expansion (repliage) de chaque message de l'historique de la
			// conversation active, indexé par position dans chat.messages ; état
			// purement transitoire (UI), remis à zéro à chaque changement de chat.
			expandedHistoryMessages: {},
			codeBeforeGeneration: "",
			// Référence "vivante" du diff (document A côté codemirror_unified_diff),
			// initialisée à codeBeforeGeneration puis mise à jour en continu au fil
			// des accept/reject par hunk (voir @update:original). Distincte de
			// codeBeforeGeneration qui, lui, doit rester la référence figée du code
			// AVANT génération, utilisée par "Revenir au code d'avant". Garder cet
			// état côté parent (pas seulement dans l'état interne de la vue
			// CodeMirror) permet de survivre à une éventuelle recréation du
			// composant/de la vue (ex. fermeture puis réouverture de la modale) sans
			// re-proposer les hunks déjà tranchés.
			diffOriginal: "",
			lastGeneratedCode: "",
			extraData: {},
			// Choix du moteur IA par chat (TODO_CODEGEN_CHAT.md section 7) : clé/
			// modèle SMD partagés (par défaut) vs clé/modèle propres au client.
			// "Ma clé" n'est proposé que si une credential chatgpt_key existe
			// réellement pour ce client/user. Détection déléguée à
			// step_credential_field.vue (RÉUTILISÉ tel quel, même composant que le
			// bouton clé de la toolbar des étapes) via ses events "status" — voir
			// onOwnCredentialStatus() — plutôt qu'une detection maison qui ne
			// reconnaissait pas une clé pourtant bien configurée.
			ownCredentialsAvailable: false,
			ownCredentialStatuses: { client: false, user: false },
			// Préférence par défaut (par utilisateur) du choix moteur IA à la
			// création d'un nouveau chat (TODO_CODEGEN_CHAT.md section 7.2),
			// stockée côté serveur/DB (table `config`, cf codegenSettingsCtrl.js) —
			// remplace l'ancien stockage localStorage (par navigateur, non
			// synchronisé entre postes/perdu au vidage du cache). Chargée une
			// fois au montage (cf fetchEngineDefault()/engineDefaultPromise).
			serverEngineDefault: { use_own_credentials: false, model_preset: "" },
			// Presets de modèle (bibliothèque "chatgpt_train"), même système que
			// steps/chatgpt.vue (section 7.3), filtrés au service "chatgpt".
			list_models: [""],
			list_models_obj: {},
			model_editor_name: "",
			model_editor_version: "",
			model_editor_is_new: true,
			model_editor_saving: false,
			model_editor_error: "",
			model_editor_loading_models: false,
			model_editor_available_models: [],
			// Mode de réponse attendu de l'IA (TODO_CODEGEN_CHAT.md section 10,
			// "Prompts discussion") : réglette 3 positions, propre à chaque chat
			// (chat.response_mode, cf makeEmptyChat/load/save). "code" = comportement
			// historique (uniquement du code, aucune explication). "code_and_answer"
			// = explication + code si pertinent. "answer" = discussion pure, le code
			// de l'étape n'est jamais modifié quoi que retourne l'IA.
			responseModeOptions: [
				{ value: 'code', text: 'Code' },
				{ value: 'code_and_answer', text: 'Code + réponse' },
				{ value: 'answer', text: 'Réponse seule' }
			],
			// Réponses en cours de saisie pour les questions de clarification en
			// attente (cf activeChatQuestions) : état purement transitoire (jamais
			// persisté), indexé par position de la question dans le tableau. Permet
			// de préparer une réponse à CHAQUE question du lot avant l'envoi (au lieu
			// d'envoyer immédiatement au premier clic sur une option suggérée).
			questionAnswers: {}
		};
	},
	computed: {
		_canGenerateIa() {
			const rules = store.state.user ? store.state.user.rules : undefined;
			if (rules === null) return true;
			return !!(rules && rules.generate_ia);
		},
		activeChat() {
			var self = this;
			return this.chats.find(function(c) { return c.uid === self.activeChatUid; }) || null;
		},
		// Colonnes disponibles (prop `columns`) filtrées par le texte tapé dans le
		// menu "Insérer une colonne" (section 10 : "insertion facile de colonnes
		// dans le prompt").
		filteredColumns() {
			var filter = (this.columnFilter || "").toLowerCase().trim();
			var cols = this.columns || [];
			if (!filter) return cols;
			return cols.filter(function(c) { return String(c).toLowerCase().indexOf(filter) !== -1; });
		},
		// Résumé affiché quand le panneau "Moteur IA" est replié (section 7,
		// UI discrète demandée) : juste assez d'info pour savoir en un coup
		// d'oeil quel moteur/modèle est actif pour la conversation courante.
		engineSummaryLabel() {
			var chat = this.activeChat;
			if (!chat) return "";
			if (!chat.use_own_credentials) return "IA partagée (SMD)";
			return "Ma clé" + (chat.model_preset ? " (" + chat.model_preset + ")" : "");
		},
		// Le choix moteur/modèle de la conversation active correspond-il déjà à
		// la préférence globale enregistrée (côté serveur, cf serverEngineDefault) ?
		// Sert à afficher soit le bouton "Définir par défaut", soit une simple
		// confirmation — et à répondre à la question "ce choix est-il juste pour
		// ce chat, ou général ?" (réponse : chat par défaut, sauf action explicite ici).
		isCurrentEngineDefault() {
			var chat = this.activeChat;
			if (!chat) return false;
			var defaultOwn = !!this.serverEngineDefault.use_own_credentials;
			var defaultPreset = this.serverEngineDefault.model_preset || "";
			if (!!chat.use_own_credentials !== defaultOwn) return false;
			if (chat.use_own_credentials && (chat.model_preset || "") !== defaultPreset) return false;
			return true;
		},
		// Aperçu (approximatif) de ce que buildOutgoingMessages() enverra réellement
		// à l'IA au prochain tour, pour rendre visible dans l'UI (section 5.1/10 :
		// demande explicite de l'utilisateur "il faut le voir graphiquement") quels
		// messages de l'historique affiché ne seront PAS transmis. Même algorithme
		// (budget de caractères en partant du plus récent), appliqué directement sur
		// `chat.messages` : approximatif seulement parce que la taille du prochain
		// prompt utilisateur n'est pas encore connue à ce stade (il n'a pas encore
		// été envoyé), donc le budget réellement disponible au moment du clic sur
		// "Générer le code" sera légèrement inférieur à celui calculé ici.
		historySendPreview() {
			var chat = this.activeChat;
			var omittedSet = {};
			if (!chat || !chat.messages || chat.messages.length <= 1)
				return { omittedSet: omittedSet, omittedCount: 0 };

			var messages = chat.messages;
			var budget = MESSAGES_HISTORY_CHAR_BUDGET;
			var keptCount = 0;
			for (var i = messages.length - 1; i >= 1; i--) {
				var len = String(messages[i].content || "").length;
				if (keptCount > 0 && len > budget)
					break;
				keptCount++;
				budget -= len;
			}
			for (var j = 1; j < messages.length - keptCount; j++)
				omittedSet[j] = true;

			return { omittedSet: omittedSet, omittedCount: messages.length - 1 - keptCount };
		},
		historyOmittedCount() {
			return this.historySendPreview.omittedCount;
		},
		// Détection de divergence (TODO section 3.1) : le code live a-t-il changé
		// depuis la dernière proposition de la conversation active ? Si oui, on
		// bloque la génération tant que l'utilisateur n'a pas fait un choix
		// explicite (continuer quand même / repartir du code actuel).
		isDivergent() {
			var chat = this.activeChat;
			if (!chat || !chat.messages || chat.messages.length === 0)
				return false;
			if (!chat.lastKnownLiveCodeHash)
				return false;
			return this.hashCode(this.value) !== chat.lastKnownLiveCodeHash;
		},
		// Question(s) de clarification en attente de réponse (TODO section 10,
		// "façon VS Code" : l'IA peut poser une ou plusieurs questions au lieu de
		// répondre directement). Dérivé directement du DERNIER message de la
		// conversation active plutôt que d'un flag persisté séparément : dès que
		// l'utilisateur répond (nouveau message poussé dans chat.messages), ce
		// dernier message n'est plus celui de l'IA, donc ce computed retombe
		// naturellement à [] sans code de "résolution" explicite à maintenir.
		activeChatQuestions() {
			var chat = this.activeChat;
			if (!chat || !chat.messages || chat.messages.length === 0)
				return [];
			var last = chat.messages[chat.messages.length - 1];
			if (!last || last.role !== 'assistant')
				return [];
			return this.parseAssistantContent(last.content, chat.response_mode).questions;
		},
		// Texte explicatif de la dernière réponse de l'IA (modes "code + réponse"
		// et "réponse seule", ou introduction d'une question de clarification),
		// affiché en évidence au-dessus du diff/code actuel plutôt que seulement
		// dans le panneau d'historique replié.
		lastAssistantText() {
			var chat = this.activeChat;
			if (!chat || !chat.messages || chat.messages.length === 0)
				return "";
			var last = chat.messages[chat.messages.length - 1];
			if (!last || last.role !== 'assistant')
				return "";
			return this.parseAssistantContent(last.content, chat.response_mode).text;
		},
		// Libellé du bouton d'envoi, adapté au contexte courant : répondre à une
		// question de clarification n'est pas la même action, pour l'utilisateur,
		// que "générer du code" (mode "réponse seule", notamment).
		sendButtonLabel() {
			if (this.activeChatQuestions.length > 0)
				return 'Répondre';
			if (this.activeChat && this.activeChat.response_mode === 'answer')
				return 'Envoyer';
			return 'Générer le code';
		},
		// Vrai dès qu'au moins une question du lot en attente a reçu une réponse
		// (option choisie ou texte libre saisi) : conditionne l'activation du
		// bouton "Envoyer les réponses" du panneau de questions (section 10),
		// pour laisser le temps de répondre à toutes les questions avant envoi.
		hasAnyQuestionAnswer() {
			var answers = this.questionAnswers || {};
			for (var k in answers) {
				if (answers[k] && String(answers[k]).trim())
					return true;
			}
			return false;
		},
		// Conditionne l'activation du bouton d'envoi principal : au moins un texte
		// à envoyer (prompt libre et/ou réponse à une question en attente), ni en
		// cours de chargement ni en divergence non résolue.
		canSendPrompt() {
			if (this.loading || this.isDivergent)
				return false;
			if (this.prompt && this.prompt.trim())
				return true;
			return this.hasAnyQuestionAnswer;
		}
	},
	watch: {
		// Mode "test en place" (inline) activé/désactivé par le parent (ex.
		// steps/fonction.vue basculant testMode) : ce composant n'est alors pas
		// re-créé (contrairement à un v-if sur le composant lui-même), donc il
		// faut réappliquer la même initialisation que openModal() (chat actif
		// garanti, credentials/presets rafraîchis) au moment où il devient visible.
		inline(val) {
			if (val)
				this.openModal();
		},
		// Nouveau lot de question(s) de clarification (ou changement de chat actif)
		// : on repart d'un état de réponses vierge. Ce watcher ne se redéclenche
		// PAS pendant la saisie dans questionAnswers (le computed ne dépend que de
		// chat.messages/response_mode), uniquement quand un nouveau message
		// assistant arrive ou que l'utilisateur change de conversation.
		activeChatQuestions: {
			immediate: true,
			handler(newQuestions) {
				var answers = {};
				for (var i = 0; i < newQuestions.length; i++)
					answers[i] = '';
				this.questionAnswers = answers;
			}
		}
	},
	mounted() {
		this.fetchModelPresets();
		// Charge la préférence par défaut du moteur IA (côté serveur) une fois au
		// montage ; la promesse est conservée pour pouvoir l'attendre dans
		// openModal() avant de créer un premier chat vide (évite une course si
		// la modale est ouverte avant la fin du fetch).
		this._engineDefaultPromise = this.fetchEngineDefault();
	},
	methods: {
		// Insère le nom d'une colonne dans le prompt, à la position du curseur
		// (remplace la sélection courante s'il y en a une) — section 10, "insertion
		// facile de colonnes dans le prompt". b-form-textarea rend directement un
		// <textarea> natif, donc son $el expose selectionStart/selectionEnd comme
		// n'importe quel textarea HTML. Si l'élément n'est pas encore disponible
		// (cas limite), on se contente d'ajouter la colonne à la fin du prompt.
		insertColumnInPrompt(col) {
			var el = this.$refs.promptInput && this.$refs.promptInput.$el;
			var current = this.prompt || "";
			if (!el || typeof el.selectionStart !== "number") {
				this.prompt = current ? current + " " + col : col;
				return;
			}

			var start = el.selectionStart;
			var end = el.selectionEnd;
			this.prompt = current.slice(0, start) + col + current.slice(end);
			this.columnFilter = "";

			var newPos = start + col.length;
			this.$nextTick(function() {
				el.focus();
				el.setSelectionRange(newPos, newPos);
			});
		},
		// Appelé par les events "status" des 2 <step_credential_field> (scope
		// client/user) réutilisés tel quel dans le template — même mécanisme,
		// déjà éprouvé, que StepCredentialsButton.vue. "Ma clé" est proposé dès
		// qu'AU MOINS un des deux scopes a la credential "chatgpt_key" configurée.
		onOwnCredentialStatus(evt) {
			this.$set(this.ownCredentialStatuses, evt.scope, evt.status === "configured");
			this.ownCredentialsAvailable = !!(this.ownCredentialStatuses.client || this.ownCredentialStatuses.user);
		},
		// Charge la préférence par défaut du moteur IA depuis le serveur (table
		// `config`, scopeée par utilisateur). En cas d'erreur (réseau, etc.), on
		// garde les valeurs par défaut déjà présentes (IA partagée SMD).
		async fetchEngineDefault() {
			try {
				var settings = await codegenSettings.getEngineDefault();
				this.serverEngineDefault = {
					use_own_credentials: !!settings.use_own_credentials,
					model_preset: settings.model_preset || ""
				};
			} catch (e) { /* ignore, garde les valeurs par défaut */ }
		},
		async fetchModelPresets() {
			try {
				var names = await library.getby_production("chatgpt_train", null, false, false, false);
				names = names.element;
				var list = [""];
				var obj = {};
				for (var i = 0; i < names.length; i++) {
					if (names[i].data.includes('"service":"chatgpt"') && !obj[names[i].name]) {
						list.push(names[i].name);
						obj[names[i].name] = names[i];
					}
				}
				this.list_models = list;
				this.list_models_obj = obj;
			} catch (e) {
				this.list_models = [""];
				this.list_models_obj = {};
			}
		},
		// Rend explicite la distinction "réglage de cette conversation" (par
		// défaut, ce choix ne concerne QUE le chat actif, cf activeChat.
		// use_own_credentials/model_preset persistés avec le chat) vs "réglage
		// général" : ce n'est qu'en cliquant ce bouton que le choix courant est
		// mémorisé côté serveur (table `config`, par utilisateur) comme valeur
		// de départ des PROCHAINES nouvelles conversations (cf makeEmptyChat()).
		async setAsDefaultEngine() {
			var chat = this.activeChat;
			if (!chat) return;
			var use_own_credentials = !!chat.use_own_credentials;
			var model_preset = use_own_credentials ? (chat.model_preset || "") : "";
			// Mise à jour optimiste (feedback immédiat), remise en cause seulement
			// si l'appel serveur échoue vraiment (voir bloc catch).
			var previous = this.serverEngineDefault;
			this.serverEngineDefault = { use_own_credentials, model_preset };
			try {
				await codegenSettings.saveEngineDefault(use_own_credentials, model_preset);
			} catch (e) {
				this.serverEngineDefault = previous;
			}
		},
		open_model_editor() {
			this.model_editor_error = "";
			this.model_editor_saving = false;
			var chat = this.activeChat;
			if (chat && chat.model_preset && this.list_models_obj[chat.model_preset]) {
				var m = JSON.parse(this.list_models_obj[chat.model_preset].data);
				this.model_editor_is_new = false;
				this.model_editor_name = chat.model_preset;
				this.model_editor_version = m.model || "";
			} else {
				this.model_editor_is_new = true;
				this.model_editor_name = "";
				this.model_editor_version = "";
			}
			this.$bvModal.show("codegen_model_editor" + this.id);
		},
		async fetch_available_models() {
			this.model_editor_loading_models = true;
			this.model_editor_error = "";
			try {
				var formData = new FormData();
				formData.append("action", "saas_back-openai");
				formData.append("parameters", JSON.stringify({ type: "list_models" }));
				var data = await workers.test(formData);
				var list = data.data.json[0].result.data;
				list.sort((a, b) => (a.created > b.created) ? -1 : 1);
				this.model_editor_available_models = [];
				for (var i = 0; i < list.length; i++) {
					if (list[i].owned_by.startsWith("user") ||
						list[i].id.startsWith("gpt-3") ||
						list[i].id.startsWith("gpt-4") ||
						list[i].id.startsWith("gpt-5") ||
						list[i].id.startsWith("o") ||
						list[i].id.startsWith("ft:")) {
						this.model_editor_available_models.push({
							value: list[i].id,
							text: list[i].id + (list[i].created ? " - " + moment(list[i].created * 1000).format("LLL") : "")
						});
					}
				}
			} catch (e) {
				this.model_editor_error = "Impossible de récupérer la liste des modèles.";
			} finally {
				this.model_editor_loading_models = false;
			}
		},
		async save_model(bvModalEvent) {
			bvModalEvent.preventDefault();
			this.model_editor_error = "";

			if (!this.model_editor_name.trim()) {
				this.model_editor_error = "Le nom est requis.";
				return;
			}
			if (!this.model_editor_version.trim()) {
				this.model_editor_error = "La version ChatGPT est requise.";
				return;
			}

			this.model_editor_saving = true;

			var existing = this.model_editor_is_new ? null : this.list_models_obj[this.model_editor_name];
			var existing_data = existing ? JSON.parse(existing.data) : {};
			var data = Object.assign({}, existing_data, {
				model: this.model_editor_version.trim(),
				service: "chatgpt"
			});

			try {
				await library.save(
					this.model_editor_name.trim(),
					new Date().toISOString(),
					data,
					"chatgpt_train",
					null
				);

				await this.fetchModelPresets();

				var name = this.model_editor_name.trim();
				if (this.activeChat) {
					this.activeChat.model_preset = name;
				}
				this.$bvModal.hide("codegen_model_editor" + this.id);
			} catch (e) {
				this.model_editor_error = "Erreur lors de l'enregistrement.";
			} finally {
				this.model_editor_saving = false;
			}
		},
		hashCode(str) {
			return CryptoJS.SHA256(str || "").toString();
		},
		hashChatContent(chat) {
			return CryptoJS.SHA256(JSON.stringify({
				m: chat.messages,
				c: chat.lastGeneratedCode,
				b: chat.codeBeforeGeneration,
				// Inclus pour que le hash change même quand seul l'état accept/reject
				// par hunk évolue (un "accept" ne modifie pas forcément
				// lastGeneratedCode, seulement diffOriginal) — sinon un tel changement
				// serait considéré à tort comme "unchanged" et diffOriginal ne serait
				// jamais (re)persisté.
				o: chat.diffOriginal
			})).toString();
		},
		makeEmptyChat() {
			// Préférence par défaut (section 7.2), chargée depuis le serveur au
			// montage (cf serverEngineDefault/fetchEngineDefault), surchargeable
			// ensuite individuellement par ce chat. "Ma clé" n'est retenu par défaut
			// que si l'option est effectivement disponible pour ce client/user.
			var defaultOwn = this.ownCredentialsAvailable && !!this.serverEngineDefault.use_own_credentials;
			var defaultPreset = this.serverEngineDefault.model_preset || "";

			return {
				uid: this.$uuid ? this.$uuid.v4() : (uniqueId("chat_") + "_" + Date.now()),
				title: "",
				messages: [],
				lastGeneratedCode: "",
				lastKnownLiveCodeHash: "",
				codeBeforeGeneration: "",
				diffOriginal: "",
				loaded: true,
				url: "",
				updated_at: null,
				_snapshotHash: "",
				use_own_credentials: defaultOwn,
				model_preset: defaultOwn ? defaultPreset : "",
				response_mode: 'code'
			};
		},
		truncateForHistory(content, maxLen) {
			var c = String(content || "");
			c = c.replace(/<\s*BEGIN_CODE\s*>/i, "[code] ").replace(/<\s*END_CODE\s*>/i, "");
			// Toujours affiché sur une seule ligne dans l'aperçu replié : les sauts
			// de ligne du contenu (code multi-lignes, prompts avec plusieurs
			// paragraphes, ...) sont remplacés par un espace, même si le résultat
			// tient largement sous la limite de troncature.
			c = c.replace(/\s*[\r\n]+\s*/g, " ").trim();
			var limit = maxLen || 300;
			if (c.length > limit)
				c = c.slice(0, limit) + "…";
			return c;
		},
		// Un message de l'historique affiché sera-t-il omis (non transmis à l'IA)
		// au prochain tour ? Sert uniquement à l'indication visuelle dans le
		// template (cf computed historySendPreview) — l'aperçu reste approximatif,
		// cf commentaire sur historySendPreview.
		isMessageOmitted(idx) {
			return !!this.historySendPreview.omittedSet[idx];
		},
		// Contenu complet (non tronqué) affiché quand un message de l'historique
		// est déplié (cf toggleHistoryMessage) — même nettoyage des tags
		// BEGIN_CODE/END_CODE que truncateForHistory, mais sans coupure.
		fullHistoryContent(content) {
			var c = String(content || "");
			return c.replace(/<\s*BEGIN_CODE\s*>/i, "[code]\n").replace(/<\s*END_CODE\s*>/i, "");
		},
		isHistoryMessageExpanded(idx) {
			return !!this.expandedHistoryMessages[idx];
		},
		toggleHistoryMessage(idx) {
			this.$set(this.expandedHistoryMessages, idx, !this.expandedHistoryMessages[idx]);
		},
		async openModal() {
			// Re-vérifie à chaque ouverture (pas seulement au montage du
			// composant, qui ne se produit qu'une fois pour toute la durée de
			// vie de la page) : la credential "chatgpt_key" ou les presets de
			// modèle peuvent avoir été ajoutés/modifiés entre-temps (ex. via le
			// bouton clé de la toolbar de l'étape) sans que ce composant, qui
			// reste monté en permanence, ne soit informé autrement. On délègue
			// directement au refresh() de step_credential_field.vue (composant
			// réutilisé, déjà éprouvé) plutôt qu'à une detection maison.
			if (this.$refs.ownCredClient) this.$refs.ownCredClient.refresh();
			if (this.$refs.ownCredUser) this.$refs.ownCredUser.refresh();
			this.fetchModelPresets();

			// Garantit qu'une conversation active existe toujours dès l'ouverture
			// de la modale (même avant toute génération) : sans ça, le choix du
			// moteur IA (section 7, gated sur activeChat) n'apparaissait nulle
			// part tant qu'aucun message n'avait encore été envoyé.
			if (!this.activeChatUid) {
				if (this.chats.length > 0) {
					this.selectChat(this.chats[0]);
				} else {
					// Attend le fetch de la préférence serveur (démarré au montage)
					// avant de créer le premier chat, pour éviter une course où le
					// chat serait créé avec les valeurs par défaut "vides" si la
					// modale est ouverte très tôt après le montage du composant.
					if (this._engineDefaultPromise) await this._engineDefaultPromise;
					this.newChat();
				}
				return;
			}
			this.stage = this.lastGeneratedCode ? 'review' : 'prompt';
		},
		newChat() {
			var chat = this.makeEmptyChat();
			this.chats.push(chat);
			this.selectChat(chat);
		},
		async ensureChatLoaded(chat) {
			if (!chat || chat.loaded || !this.loadChat)
				return;
			this.loading = true;
			try {
				var full = await this.loadChat({ uid: chat.uid, url: chat.url });
				if (full) {
					chat.messages = full.messages || [];
					chat.lastGeneratedCode = full.lastGeneratedCode || "";
					chat.lastKnownLiveCodeHash = full.lastKnownLiveCodeHash || "";
					chat.codeBeforeGeneration = full.codeBeforeGeneration || "";
					// État accept/reject par hunk persisté (cf save()) : on le restaure
					// tel quel s'il est présent. Fallback "tout accepté" (= code final)
					// uniquement pour les anciennes données sauvegardées avant l'ajout de
					// ce champ, qui n'ont jamais eu cette granularité.
					chat.diffOriginal = full.diffOriginal || full.lastGeneratedCode || full.codeBeforeGeneration || "";
					chat.response_mode = full.response_mode || 'code';
				}
				chat.loaded = true;
				chat._snapshotHash = this.hashChatContent(chat);
			} catch (e) {
				this.error = "Impossible de charger cette conversation : " + (e && e.message ? e.message : e);
			} finally {
				this.loading = false;
			}
		},
		async selectChat(chatOrUid) {
			var chat = typeof chatOrUid === 'string'
				? this.chats.find(function(c) { return c.uid === chatOrUid; })
				: chatOrUid;
			if (!chat)
				return;

			await this.ensureChatLoaded(chat);

			this.activeChatUid = chat.uid;
			this.prompt = "";
			this.error = "";
			this.expandedHistoryMessages = {};
			this.codeBeforeGeneration = chat.codeBeforeGeneration || "";
			this.diffOriginal = chat.diffOriginal || chat.codeBeforeGeneration || "";
			this.lastGeneratedCode = chat.lastGeneratedCode || "";

			if (this.validateCode)
				this.validation_errors = this.lastGeneratedCode ? this.validateCode(this.lastGeneratedCode) : [];
			else
				this.validation_errors = this.lastGeneratedCode ? this.defaultValidateCode(this.lastGeneratedCode) : [];

			this.stage = this.lastGeneratedCode ? 'review' : 'prompt';
		},
		async removeChat(chat) {
			// eslint-disable-next-line no-alert
			if (!window.confirm("Supprimer définitivement cette conversation ? Cette action est irréversible."))
				return;

			if (this.deleteChat) {
				try {
					await this.deleteChat({ uid: chat.uid, url: chat.url });
				} catch (e) {
					this.error = "Erreur lors de la suppression de la conversation : " + (e && e.message ? e.message : e);
					return;
				}
			}

			var idx = this.chats.findIndex(function(c) { return c.uid === chat.uid; });
			if (idx !== -1)
				this.chats.splice(idx, 1);

			if (this.activeChatUid === chat.uid) {
				if (this.chats.length > 0) {
					this.selectChat(this.chats[0]);
				} else {
					// Toujours garder une conversation active tant que la modale est
					// ouverte (cf openModal) — sinon le choix du moteur IA disparaît.
					this.newChat();
				}
			}
		},
		// Renommage du titre d'un chat (section 10) : édition inline, déclenchée
		// par le bouton crayon ou un double-clic sur le titre. Ne persiste rien
		// tout de suite : le nouveau titre reste en mémoire (chat.title) et sera
		// écrit au prochain save() du parent, comme le reste de l'état du chat.
		startChatTitleEdit(chat) {
			this.editingChatUid = chat.uid;
			this.editingChatTitle = chat.title || "";
		},
		commitChatTitle(chat) {
			// @blur se déclenche aussi après Enter/Esc (changement de focus) : ne
			// rien faire si ce chat n'est déjà plus en cours d'édition.
			if (this.editingChatUid !== chat.uid)
				return;
			chat.title = this.editingChatTitle.trim();
			this.editingChatUid = "";
			this.editingChatTitle = "";
		},
		cancelChatTitleEdit() {
			this.editingChatUid = "";
			this.editingChatTitle = "";
		},
		applyGeneratedCode() {
			if (this.validation_errors.length > 0)
				return;

			// Utilise la valeur courante de l'éditeur de diff (après d'éventuels
			// accept/reject par hunk), pas forcément identique au code généré brut.
			var final_code = this.$refs.diffEditor ? this.$refs.diffEditor.getValue() : this.lastGeneratedCode;

			this.$emit('input', final_code);

			// Ce changement du code live vient de cette conversation elle-même
			// (pas d'une édition externe) : on resynchronise lastKnownLiveCodeHash
			// avec le code désormais appliqué, sinon le bandeau de divergence
			// (isDivergent) se déclenchait à tort juste après avoir appliqué.
			if (this.activeChat)
				this.activeChat.lastKnownLiveCodeHash = this.hashCode(final_code);

			this.$notify({
				group: "info",
				text: "Code généré appliqué avec succès",
				type: "success",
				duration: 3000
			});
		},
		acceptAllHunks() {
			if (this.$refs.diffEditor)
				this.$refs.diffEditor.acceptAll();
		},
		rejectAllHunks() {
			if (this.$refs.diffEditor)
				this.$refs.diffEditor.rejectAll();
		},
		onDiffInput(new_code) {
			// Re-valide en direct le code au fur et à mesure des accept/reject par
			// hunk (ou d'une édition manuelle dans l'éditeur de diff), et tient
			// lastGeneratedCode à jour (utilisé par save()/load() et par le bouton
			// "Appliquer" en filet de secours si l'éditeur n'est pas monté).
			this.lastGeneratedCode = new_code;
			if (this.activeChat)
				this.activeChat.lastGeneratedCode = new_code;

			if (this.validateCode)
				this.validation_errors = this.validateCode(new_code);
			else
				this.validation_errors = this.defaultValidateCode(new_code);
		},
		onDiffOriginalUpdate(newOriginal) {
			if (this.activeChat)
				this.activeChat.diffOriginal = newOriginal;
		},
		revertToCodeBeforeGeneration() {
			if (!this.codeBeforeGeneration)
				return;

			var chat = this.activeChat;

			this.$emit('input', this.codeBeforeGeneration);

			// Repasse en stage 'prompt' : le diff affichait encore le code
			// désormais annulé, ce qui ne reflétait pas le revert dans la fenêtre
			// de test. Il n'y a plus rien à comparer/trancher tant qu'aucune
			// nouvelle génération n'a eu lieu — la vue en lecture seule (stage
			// 'prompt') affiche alors directement le code redevenu actuel.
			// L'historique de la conversation (chat.messages), lui, n'est PAS
			// touché : il reste utile si l'utilisateur enchaîne avec une autre
			// demande juste après (l'IA garde le contexte de ce qui a déjà été
			// proposé/rejeté). `chat.lastGeneratedCode` est simplement remis à ""
			// pour que le prochain tour reparte bien du code live actuel
			// (`currentCodeForContext` dans generate() retombe alors sur
			// `this.value`, cf commentaire associé) plutôt que de renvoyer le code
			// désormais abandonné.
			this.lastGeneratedCode = "";
			this.diffOriginal = this.codeBeforeGeneration;
			this.validation_errors = [];
			this.stage = 'prompt';

			if (chat) {
				chat.lastGeneratedCode = "";
				chat.diffOriginal = this.codeBeforeGeneration;
				// Même raison que dans applyGeneratedCode() : ce changement du code
				// live est initié par ce composant, pas par une édition externe.
				chat.lastKnownLiveCodeHash = this.hashCode(this.codeBeforeGeneration);
			}
		},
		stripCodeFences(str) {
			var s = String(str || "").trim();
			if (s.startsWith("```")) {
				s = s.replace(/^```[a-zA-Z]*\n?/, "");
				s = s.replace(/```\s*$/, "");
			}
			return s.trim();
		},
		// Analyse la réponse brute de l'IA selon le "contrat de format" envoyé
		// dans buildProtocolMessage() : un éventuel bloc de questions de
		// clarification (<BEGIN_QUESTIONS>), un éventuel bloc de code
		// (<BEGIN_CODE>), le reste étant considéré comme le texte explicatif
		// (modes "code + réponse"/"réponse seule", ou introduction d'une
		// question). Utilisé à la fois juste après generate() (pour décider quoi
		// stocker/afficher) et par les computed activeChatQuestions/
		// lastAssistantText (pour re-dériver le même état depuis un message déjà
		// stocké, sans avoir besoin de champs persistés séparés).
		parseAssistantContent(raw, mode) {
			var s = String(raw || "");
			var questions = [];

			var qMatch = s.match(/<\s*BEGIN_QUESTIONS\s*>([\s\S]*?)<\s*END_QUESTIONS\s*>/i);
			if (qMatch) {
				var qRe = /<\s*QUESTION\s*>([\s\S]*?)<\s*\/\s*QUESTION\s*>/gi;
				var qm;
				while ((qm = qRe.exec(qMatch[1])) !== null) {
					var lines = qm[1].split(/\r?\n/);
					var options = [];
					var questionLines = [];
					for (var i = 0; i < lines.length; i++) {
						var optMatch = lines[i].match(/^\s*<\s*OPTION\s*>\s*(.*)$/i);
						if (optMatch) {
							if (optMatch[1].trim())
								options.push(optMatch[1].trim());
						} else if (lines[i].trim()) {
							questionLines.push(lines[i].trim());
						}
					}
					var questionText = questionLines.join(" ").trim();
					if (questionText)
						questions.push({ question: questionText, options: options });
				}
				s = s.replace(qMatch[0], "");
			}

			var code = null;
			if (mode === 'answer') {
				// Mode "réponse seule" : dans AUCUN cas le code de l'étape ne doit
				// être modifié — on ignore/retire tout bloc de code que l'IA aurait
				// pu renvoyer par erreur, plutôt que de risquer de l'exposer/l'appliquer.
				s = s.replace(/<\s*BEGIN_CODE\s*>[\s\S]*?<\s*END_CODE\s*>/gi, "");
			} else {
				var codeMatch = s.match(/<\s*BEGIN_CODE\s*>([\s\S]*?)<\s*END_CODE\s*>/i);
				if (!codeMatch)
					codeMatch = s.match(/&lt;\s*BEGIN_CODE\s*&gt;([\s\S]*?)&lt;\s*END_CODE\s*&gt;/i);
				if (codeMatch && codeMatch[1] != null) {
					code = this.stripCodeFences(codeMatch[1]);
					s = s.replace(codeMatch[0], "");
				} else if (mode === 'code' && questions.length === 0) {
					// Compat. avec le comportement historique (mode "code" par
					// défaut, avant l'ajout de ce contrat de format) : si l'IA n'a
					// pas utilisé les balises explicites, toute la réponse EST le
					// code (sauf si une question de clarification a été détectée).
					code = this.stripCodeFences(s);
					s = "";
				}
			}

			return { text: s.trim(), code: code, questions: questions };
		},
		// Sérialise la réponse analysée en un contenu de message normalisé
		// (toujours les mêmes balises, quel que soit ce que l'IA a réellement
		// utilisé) — stocké tel quel dans chat.messages, ce qui permet de
		// re-parser fidèlement plus tard (reload, computed activeChatQuestions/
		// lastAssistantText) et reste compatible avec le retrait des blocs de
		// code des tours précédents dans buildOutgoingMessages().
		serializeAssistantMessage(parsed) {
			var parts = [];
			if (parsed.text)
				parts.push(parsed.text);
			if (parsed.code !== null)
				parts.push('<BEGIN_CODE>\n' + parsed.code + '\n<END_CODE>');
			if (parsed.questions && parsed.questions.length > 0) {
				var qBlock = parsed.questions.map(function(q) {
					var optLines = q.options.map(function(o) { return '<OPTION>' + o; }).join('\n');
					return '<QUESTION>\n' + q.question + (optLines ? '\n' + optLines : '') + '\n</QUESTION>';
				}).join('\n');
				parts.push('<BEGIN_QUESTIONS>\n' + qBlock + '\n<END_QUESTIONS>');
			}
			return parts.join('\n\n');
		},
		// Instructions de format envoyées FRAÎCHEMENT à chaque appel (jamais
		// stockées dans chat.messages : ce n'est pas un échange réel, juste un
		// rappel de contrat) — s'applique uniformément quel que soit le
		// buildRequest spécifique du parent (steps/fonction.vue, dashboard/*),
		// y compris aux tours suivants qui, eux, ne renvoient pas les
		// instructions du 1er tour. Couvre les 3 modes de la réglette (section
		// 10) et le mécanisme de question de clarification façon VS Code.
		buildProtocolMessage(mode) {
			var modeInstructions;
			if (mode === 'answer') {
				modeInstructions = "Mode \"réponse seule\" : ne génère JAMAIS de code, quelle que soit la demande. " +
					"N'inclus jamais de bloc <BEGIN_CODE>...<END_CODE>. Réponds uniquement par du texte explicatif en langage naturel.";
			} else if (mode === 'code_and_answer') {
				modeInstructions = "Mode \"code + réponse\" : réponds d'abord par une courte explication en texte libre, " +
					"puis, si une modification de code est pertinente, ajoute le code COMPLET final dans un bloc " +
					"<BEGIN_CODE>\n...\n<END_CODE> après cette explication. N'ajoute pas de bloc de code si la demande " +
					"était une simple question sans impact sur le code.";
			} else {
				modeInstructions = "Mode \"code\" : ta réponse doit être UNIQUEMENT le code complet, dans un bloc " +
					"<BEGIN_CODE>\n...\n<END_CODE>, sans aucun texte en dehors (sauf si tu poses une question de " +
					"clarification, voir ci-dessous).";
			}

			return {
				role: 'system',
				content:
`Contrat de format de réponse à respecter strictement :

${modeInstructions}

Question de clarification : si la demande de l'utilisateur est ambiguë ou qu'il te manque une information essentielle pour répondre correctement, tu peux poser une ou plusieurs questions de clarification AU LIEU de répondre directement. Dans ce cas, n'inclus AUCUN bloc <BEGIN_CODE> dans cette réponse (attends la réponse de l'utilisateur au tour suivant). Format strict à utiliser :
<BEGIN_QUESTIONS>
<QUESTION>
Texte de la première question ?
<OPTION>Réponse suggérée 1
<OPTION>Réponse suggérée 2
</QUESTION>
<QUESTION>
Texte d'une éventuelle deuxième question ?
<OPTION>Oui
<OPTION>Non
</QUESTION>
<END_QUESTIONS>
Chaque question doit avoir au moins une <OPTION>, mais l'utilisateur pourra aussi répondre librement en texte libre plutôt que choisir une option proposée.`
			};
		},
		// Sélectionne une réponse suggérée pour UNE question du lot en attente
		// (cf activeChatQuestions) : ne fait que préparer la réponse (mise en
		// évidence du bouton choisi + remplissage du champ libre associé, encore
		// modifiable) ; l'envoi se fait via le bouton principal (generate()), qui
		// combine ces réponses avec un éventuel texte libre saisi dans le prompt.
		selectQuestionOption(qIdx, opt) {
			if (this.loading)
				return;
			this.questionAnswers[qIdx] = opt;
		},
		// Compose le contenu effectivement envoyé à l'IA au clic sur le bouton
		// d'envoi principal : réponses préparées pour les questions de
		// clarification en attente (cf questionAnswers/activeChatQuestions) ET,
		// s'il y en a un, le texte libre du champ prompt (précisions supplémentaires)
		// — tout est envoyé en un seul message, aucun bouton séparé nécessaire.
		composeUserContent() {
			var parts = [];
			var questions = this.activeChatQuestions;
			for (var i = 0; i < questions.length; i++) {
				var answer = (this.questionAnswers[i] || '').trim();
				if (answer)
					parts.push(questions[i].question + '\n' + answer);
			}
			if (this.prompt && this.prompt.trim())
				parts.push(this.prompt.trim());
			return parts.join('\n\n');
		},
		defaultValidateCode(code) {
			var errors = [];
			var c = (code || "").trim();
			if (!c)
				errors.push("Le code généré est vide.");

			var forbidden = [
				{ re: /\basync\b/, msg: "async est interdit (vm2, pas d'asynchrone)." },
				{ re: /\bawait\b/, msg: "await est interdit (vm2, pas d'asynchrone)." },
				{ re: /\bimport\b/, msg: "import est interdit." },
				{ re: /\bexport\b/, msg: "export est interdit." },
				{ re: /\brequire\s*\(/, msg: "require() est interdit." },
				{ re: /\bfetch\s*\(/, msg: "fetch() est interdit." },
				{ re: /XMLHttpRequest/, msg: "XMLHttpRequest est interdit." },
			];
			for (var i = 0; i < forbidden.length; i++) {
				if (forbidden[i].re.test(c))
					errors.push(forbidden[i].msg);
			}

			// Validation syntaxique
			try {
				// eslint-disable-next-line no-new-func
				new Function("\"use strict\";\n" + c);
			} catch (e) {
				errors.push("Erreur de syntaxe JavaScript: " + (e && e.message ? e.message : e));
			}

			return errors;
		},
		// Construit le tableau de messages effectivement envoyé à l'IA à partir
		// de l'historique complet d'un chat + du nouveau message utilisateur :
		// fenêtre glissante (perf, TODO 5.1) en gardant toujours épinglé le tout
		// premier message (l'ancrage : instructions + code de référence), et en
		// remplissant le reste du budget (MESSAGES_HISTORY_CHAR_BUDGET) avec les
		// échanges les PLUS RÉCENTS en remontant depuis le nouveau message (donc
		// toujours envoyé en entier, jamais compté dans le budget). Si des
		// messages intermédiaires ne tiennent pas dans le budget, ils sont omis
		// mais **signalés explicitement à l'IA** via un message `role: 'system'`
		// inséré entre l'ancrage et la suite conservée — sans ça, l'IA n'a aucun
		// moyen de savoir qu'une partie de la conversation lui est cachée et
		// pourrait raisonner à tort comme si la conversation était continue.
		buildOutgoingMessages(history, newMessage) {
			var full = history.concat([newMessage]);
			if (full.length <= 1)
				return full;

			// Une seule "photo" de code doit être envoyée par requête : celle jointe
			// au tout dernier message (le plus récent, toujours `newMessage` ici).
			// Tout bloc <BEGIN_CODE>...<END_CODE> trouvé dans un message PRÉCÉDENT
			// (ancrage du 1er tour inclus, réponses passées de l'IA incluses) est
			// retiré et remplacé par une courte mention — sinon chaque tour
			// accumulerait une nouvelle copie complète du code (coût/latence,
			// section 5) tout en risquant de laisser l'IA se baser sur une version
			// périmée (ex. après un accept/reject de hunk) plutôt que sur celle,
			// à jour, jointe au message le plus récent.
			var stripped = full.map(function(m, idx) {
				if (idx === full.length - 1)
					return m;
				var content = String(m.content || "");
				if (!/<BEGIN_CODE>[\s\S]*?<END_CODE>/i.test(content))
					return m;
				var newContent = content.replace(/<BEGIN_CODE>[\s\S]*?<END_CODE>/gi,
					"[Extrait de code omis ici — se référer au code joint au message le plus récent.]");
				return Object.assign({}, m, { content: newContent });
			});

			var anchor = stripped[0];
			var rest = stripped.slice(1); // contient au moins newMessage (toujours conservé)
			var budget = MESSAGES_HISTORY_CHAR_BUDGET;
			var kept = [];
			for (var i = rest.length - 1; i >= 0; i--) {
				var len = String(rest[i].content || "").length;
				// Le tout dernier message (le nouveau tour envoyé) est toujours
				// inclus intégralement, même s'il dépasse le budget à lui seul.
				if (kept.length > 0 && len > budget)
					break;
				kept.unshift(rest[i]);
				budget -= len;
			}

			var omittedCount = rest.length - kept.length;
			var result = [anchor];
			if (omittedCount > 0) {
				result.push({
					role: 'system',
					content: "[Note technique : " + omittedCount + " message(s) intermédiaire(s) de cette conversation ont été omis ici " +
						"pour rester dans une taille raisonnable. Seuls le tout premier message ci-dessus (instructions + code de référence) " +
						"et les échanges les plus récents ci-dessous sont transmis dans cette requête. Si le contexte te semble incomplet " +
						"ou qu'une information mentionnée récemment par l'utilisateur te manque, n'hésite pas à le signaler ou à lui demander " +
						"de la répéter plutôt que de supposer.]"
				});
			}
			return result.concat(kept);
		},
		// Boutons du bandeau de divergence ("Continuer quand même" / "Repartir du
		// code actuel") : ces boutons ne déclenchent PAS de génération — ils se
		// contentent de résoudre localement la divergence détectée (mettre à jour
		// la référence de code / l'historique), exactement comme le ferait un
		// clic sur "Fermer" d'un avertissement. L'utilisateur choisit ensuite lui-
		// même quand envoyer son prompt via le bouton "Générer le code" normal.
		// - 'continue' : ne touche à rien d'autre que le hash de référence — la
		//   conversation continue avec son historique existant tel quel (l'IA ne
		//   "connaît" le code que via l'ancrage initial + ses propres réponses
		//   précédentes, jamais via une resynchronisation automatique).
		// - 'rebase' : garde l'historique de la conversation intact (contrairement
		//   à la première implémentation qui le vidait) et se contente d'ajouter un
		//   tour utilisateur "synthétique" indiquant le nouveau code actuel — l'IA
		//   en tiendra compte au prochain tour comme si l'utilisateur l'avait
		//   explicitement signalé, sans perdre le contexte de la conversation.
		//   Repositionne aussi les références de code (diff, "code avant
		//   génération") sur le code actuel de l'éditeur Ace (`this.value`) ; le
		//   diff/CodeMirror redisparaît (retour en stage 'prompt') puisqu'il n'y a
		//   plus rien à comparer tant qu'aucune nouvelle génération n'a eu lieu.
		resolveDivergence(mode) {
			var chat = this.activeChat;
			if (!chat)
				return;

			if (mode === 'rebase') {
				chat.messages.push({
					role: 'user',
					content: "Le code de l'étape a été modifié en dehors de cette conversation " +
						"(édition manuelle ou une autre génération). Voici le nouveau code actuel, " +
						"à partir duquel il faut continuer :\n<BEGIN_CODE>\n" + this.value + "\n<END_CODE>"
				});
				chat.lastGeneratedCode = "";
				chat.diffOriginal = this.value;
				chat.codeBeforeGeneration = this.value;
				this.lastGeneratedCode = "";
				this.diffOriginal = this.value;
				this.codeBeforeGeneration = this.value;
				this.validation_errors = [];
				this.stage = 'prompt';
			}

			chat.lastKnownLiveCodeHash = this.hashCode(this.value);
		},
		async generate() {
			this.error = "";
			this.validation_errors = [];
			// Contenu effectif envoyé : réponses aux questions de clarification en
			// attente (le cas échéant) + texte libre du prompt, combinés en un seul
			// message (cf composeUserContent).
			var contentToSend = this.composeUserContent();
			if (!contentToSend)
				return;

			if (!this.activeChatUid) {
				var chat0 = this.makeEmptyChat();
				this.chats.push(chat0);
				this.activeChatUid = chat0.uid;
			}
			var chat = this.activeChat;
			if (!chat)
				return;

			// Divergence non résolue (TODO 3.1) : on attend un choix explicite de
			// l'utilisateur via le bandeau d'avertissement avant de générer quoi que
			// ce soit (resolveDivergence() s'en charge et remet isDivergent à false).
			if (this.isDivergent)
				return;

			var isFirstTurn = chat.messages.length === 0;

			// À partir du 2e tour, on rejoint quand même une "photo" du code actuel
			// à chaque prompt (buildOutgoingMessages se charge de retirer celles des
			// tours précédents avant l'envoi, cf plus bas) : sans ça, l'IA ne
			// connaissait le code qu'à travers l'ancrage figé du 1er tour + ses
			// propres réponses passées, jamais mis à jour par un accept/reject de
			// hunk ou une divergence résolue via "Continuer quand même". On utilise
			// en priorité `lastGeneratedCode` (état courant du diff de CETTE
			// conversation, incluant les accept/reject déjà faits), et seulement à
			// défaut le code live de l'éditeur (`this.value`) — ex. juste après un
			// rebase, où `lastGeneratedCode` est réinitialisé à "".
			var currentCodeForContext = chat.lastGeneratedCode || this.value;

			var userTurnContent = isFirstTurn
				? this.buildRequest(contentToSend, this.value)
				: contentToSend + "\n\n[Code actuel de l'étape, pour référence :]\n<BEGIN_CODE>\n" + currentCodeForContext + "\n<END_CODE>";

			// Capture du code live au moment de la demande (référence pour le
			// bouton "Annuler ce dernier prompt" — qui restaure le code RÉEL de
			// l'éditeur, cf revertToCodeBeforeGeneration — et pour la détection de
			// divergence du PROCHAIN tour).
			var liveCodeAtRequest = this.value;
			var liveHashAtRequest = this.hashCode(liveCodeAtRequest);
			this.codeBeforeGeneration = liveCodeAtRequest;
			chat.codeBeforeGeneration = liveCodeAtRequest;

			// Base du diff ("avant", panneau de gauche) pour CE tour : on reprend
			// `currentCodeForContext` (donc en priorité le dernier code généré de
			// CETTE conversation, pas encore appliqué à l'étape) plutôt que
			// `liveCodeAtRequest`/`this.value`. Sinon, tant que l'utilisateur
			// n'avait pas cliqué sur "Appliquer le code généré" entre deux
			// prompts, chaque nouveau tour effaçait la proposition en attente de
			// la base du diff et repartait du code réellement en vigueur sur
			// l'étape (souvent bien plus ancien), donnant l'impression que le
			// tour précédent de la conversation n'était pas pris en compte.
			this.diffOriginal = currentCodeForContext;
			chat.diffOriginal = currentCodeForContext;

			var outgoingMessages = this.buildOutgoingMessages(chat.messages, { role: 'user', content: userTurnContent });
			// Contrat de format de réponse (mode + questions de clarification,
			// section 10) : injecté frais à chaque appel, jamais stocké dans
			// l'historique persisté (cf buildProtocolMessage).
			outgoingMessages = [this.buildProtocolMessage(chat.response_mode)].concat(outgoingMessages);

			// Choix du moteur IA (TODO_CODEGEN_CHAT.md section 7) : la clé et le
			// modèle sont toujours résolus/validés côté back (jamais de confiance
			// dans un modèle brut envoyé par le front) — on n'envoie ici que le
			// choix explicite (SMD vs clé propre) et, en mode clé propre, le nom
			// du preset chatgpt_train sélectionné.
			var engineParams = chat.use_own_credentials
				? { use_smd: false, chatgpt_train: chat.model_preset || "" }
				: { use_smd: true };

			this.loading = true;
			try {
				var formData = new FormData();
				formData.append("action", "saas_back-openai");
				formData.append(
					"parameters",
					JSON.stringify(Object.assign({
						config: "fonction code",
						template: false,
						type: "messages",
						content: "messages",
						messages: "messages",
						column_result: "result",
						column_error: "error"
						// Le modèle est résolu/validé côté back (cf section 7.4) —
						// le front ne fait qu'indiquer le choix moteur ci-dessous.
					}, engineParams))
				);

				var data = [{ messages: JSON.stringify(outgoingMessages) }];
				var str_csv = json2csv.parse(data, {
					fields: Object.keys(data[0]),
					fieldNames: Object.keys(data[0]),
				});

				var files = [
					new File([str_csv], "data.csv", {
						type: "text/csv",
					}),
				];
				formData.append(files[0].name, files[0], "data.csv");

				var result = await workers.test(formData);
				var csv_data = await treatments.getFileAlyx(
					result.data.files[0].url,
					false,
					false
				);
				var parsedCsv = this.$papa.parse(csv_data, { header: true });
				var rows = (parsedCsv && parsedCsv.data) ? parsedCsv.data : [];
				var raw = rows && rows[0] ? (rows[0].result || "") : "";

				// Analyse la réponse selon le mode de la conversation (section 10) :
				// code pur / code + explication / discussion pure, plus l'éventuel
				// bloc de question(s) de clarification (cf parseAssistantContent).
				var parsedResponse = this.parseAssistantContent(raw, chat.response_mode);
				var assistantContent = this.serializeAssistantMessage(parsedResponse);

				chat.messages.push({ role: 'user', content: userTurnContent });
				chat.messages.push({ role: 'assistant', content: assistantContent });
				chat.lastKnownLiveCodeHash = liveHashAtRequest;
				if (!chat.title)
					chat.title = contentToSend.length > 40 ? (contentToSend.slice(0, 40) + '…') : contentToSend;

				if (parsedResponse.code !== null) {
					chat.lastGeneratedCode = parsedResponse.code;
					this.lastGeneratedCode = parsedResponse.code;

					// Utiliser la fonction de validation fournie ou la fonction par défaut
					if (this.validateCode) {
						this.validation_errors = this.validateCode(parsedResponse.code);
					} else {
						this.validation_errors = this.defaultValidateCode(parsedResponse.code);
					}

					this.stage = 'review';
				} else {
					// Pas de code proposé (mode "réponse seule", tour de discussion en
					// mode "code + réponse", ou question de clarification en attente,
					// cf activeChatQuestions) : rien à comparer/appliquer, on reste en
					// 'prompt' — la réponse de l'IA reste visible ci-dessus
					// (lastAssistantText) et dans l'historique de la conversation.
					chat.lastGeneratedCode = "";
					this.lastGeneratedCode = "";
					this.validation_errors = [];
					this.stage = 'prompt';
				}

				this.prompt = "";
				this.questionAnswers = {};
			} catch (e) {
				this.error = e && e.message ? e.message : String(e);
			} finally {
				this.loading = false;
			}
		},
		save(extraData) {
			if (extraData) {
				this.extraData = extraData;
			}

			var self = this;
			var chatsPayload = this.chats
				.filter(function(chat) {
					// Ne pas persister un chat resté totalement vide (jamais envoyé
					// de message) — openModal()/removeChat() en créent un par défaut
					// systématiquement pour que le choix du moteur IA soit toujours
					// visible ; ça ne doit pas polluer la sauvegarde avec des
					// conversations "(sans titre)" fantômes.
					return chat.messages.length > 0 || chat.url;
				})
				.map(function(chat) {
					var currentHash = self.hashChatContent(chat);
					var unchanged = chat.loaded && chat.url && chat._snapshotHash && chat._snapshotHash === currentHash;
					if (unchanged) {
						return {
							uid: chat.uid,
							title: chat.title,
							url: chat.url,
							updated_at: chat.updated_at,
							use_own_credentials: !!chat.use_own_credentials,
							model_preset: chat.model_preset || "",
							response_mode: chat.response_mode || 'code'
						};
					}

					// Commit optimiste : ce contenu sera envoyé en entier pour être
					// (re)persisté par ce save() (cf scenarioCtrl.save côté back qui
					// écrit sur S3 tout chat dont le payload contient "messages").
					chat._snapshotHash = currentHash;
					return {
						uid: chat.uid,
						title: chat.title,
						messages: clone(chat.messages),
						lastGeneratedCode: chat.lastGeneratedCode,
						lastKnownLiveCodeHash: chat.lastKnownLiveCodeHash,
						codeBeforeGeneration: chat.codeBeforeGeneration,
						// État accept/reject par hunk (cf onDiffOriginalUpdate) : sans ça,
						// tout hunk déjà tranché réapparaîtrait comme pending au rechargement.
						diffOriginal: chat.diffOriginal,
						updated_at: chat.updated_at || null,
						use_own_credentials: !!chat.use_own_credentials,
						model_preset: chat.model_preset || "",
						response_mode: chat.response_mode || 'code'
					};
				});

			return {
				prompt: this.prompt,
				activeChatUid: this.activeChatUid,
				chats: chatsPayload,
				extraData: this.extraData
			};
		},
		load(data) {
			if (!data)
				return this.save();

			if (data.prompt)
				this.prompt = data.prompt;
			if (data.extraData)
				this.extraData = data.extraData;

			var self = this;
			this.chats = ((data.chats) || []).map(function(c) {
				var loaded = !!c.messages;
				var chat = {
					uid: c.uid || (self.$uuid ? self.$uuid.v4() : uniqueId("chat_")),
					title: c.title || "",
					messages: c.messages || [],
					lastGeneratedCode: c.lastGeneratedCode || "",
					lastKnownLiveCodeHash: c.lastKnownLiveCodeHash || "",
					codeBeforeGeneration: c.codeBeforeGeneration || "",
					// État accept/reject par hunk persisté (cf save()) : restauré tel
					// quel s'il est présent, sinon fallback "tout accepté" (anciennes
					// données sauvegardées avant l'ajout de ce champ).
					diffOriginal: c.diffOriginal || c.lastGeneratedCode || c.codeBeforeGeneration || "",
					loaded: loaded,
					url: c.url || "",
					updated_at: c.updated_at || null,
					_snapshotHash: "",
					use_own_credentials: !!c.use_own_credentials,
					model_preset: c.model_preset || "",
					response_mode: c.response_mode || 'code'
				};
				if (loaded)
					chat._snapshotHash = self.hashChatContent(chat);
				return chat;
			});

			// Compat. ascendante : anciens scénarios enregistrés avant
			// l'introduction du multi-chat (sections 3/4), avec seulement les 3
			// champs plats historiques.
			if (this.chats.length === 0 && (data.codeBeforeGeneration || data.lastGeneratedCode)) {
				var legacyChat = this.makeEmptyChat();
				legacyChat.title = "Conversation";
				legacyChat.codeBeforeGeneration = data.codeBeforeGeneration || "";
				legacyChat.diffOriginal = data.lastGeneratedCode || data.codeBeforeGeneration || "";
				if (data.lastGeneratedCode) {
					legacyChat.messages = [
						{ role: 'user', content: data.prompt || '' },
						{ role: 'assistant', content: '<BEGIN_CODE>\n' + data.lastGeneratedCode + '\n<END_CODE>' }
					];
					legacyChat.lastGeneratedCode = data.lastGeneratedCode;
				}
				legacyChat._snapshotHash = this.hashChatContent(legacyChat);
				this.chats = [legacyChat];
			}

			var activeUid = (data.activeChatUid && this.chats.some(function(c) { return c.uid === data.activeChatUid; }))
				? data.activeChatUid
				: (this.chats[0] ? this.chats[0].uid : "");

			if (activeUid) {
				this.activeChatUid = activeUid;
				var chat = this.chats.find(function(c) { return c.uid === activeUid; });
				this.codeBeforeGeneration = chat.codeBeforeGeneration;
				this.diffOriginal = chat.diffOriginal || chat.codeBeforeGeneration;
				this.lastGeneratedCode = chat.lastGeneratedCode;
				if (this.validateCode)
					this.validation_errors = this.lastGeneratedCode ? this.validateCode(this.lastGeneratedCode) : [];
				else
					this.validation_errors = this.lastGeneratedCode ? this.defaultValidateCode(this.lastGeneratedCode) : [];
				this.stage = this.lastGeneratedCode ? 'review' : 'prompt';
			} else {
				this.activeChatUid = "";
				this.stage = 'prompt';
			}

			// Retourner les données pour que le parent puisse les utiliser
			return this.save();
		}
	}
};
</script>

<style scoped>
</style>

